import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { CRow, CCol, CCard, CCardHeader, CCardBody } from "@coreui/react";
import CustomTimeline from "react-fast-timeline";
import "react-fast-timeline/style.css";

const GarbageTimelineSection = ({
  dateRangeForVis,
  statusTimelineItems = [],
  sitesTimelineItems = [],
  handleTimeDoubleClick,
  currentTimeMs,
}) => {
  const [isSynced, setIsSynced] = useState(true);
  const [bounds1, setBounds1] = useState({ start: null, end: null });
  const [bounds2, setBounds2] = useState({ start: null, end: null });
  const [timelineRenderKey, setTimelineRenderKey] = useState(0);
  const isZoomedRef = useRef(false);

  const rangeStartMs = dateRangeForVis?.start ? dateRangeForVis.start.getTime() : null;
  const rangeEndMs = dateRangeForVis?.end ? dateRangeForVis.end.getTime() : null;

  // Сбрасываем ручной зум при каждой смене даты или приходе новых данных
  useEffect(() => {
    isZoomedRef.current = false;
  }, [rangeStartMs, rangeEndMs, statusTimelineItems, sitesTimelineItems]);

  // Пересчитываем границы шкалы при смене даты или данных
  useEffect(() => {
    let minStart = Infinity;
    let maxEnd = -Infinity;

    statusTimelineItems.forEach((item) => {
      if (item.start < minStart) minStart = item.start;
      if (item.end > maxEnd) maxEnd = item.end;
    });

    sitesTimelineItems.forEach((item) => {
      if (item.start < minStart) minStart = item.start;
      if (item.end > maxEnd) maxEnd = item.end;
    });

    if (minStart !== Infinity && maxEnd !== -Infinity) {
      const duration = maxEnd - minStart;
      const padding = Math.max(duration * 0.05, 15 * 60 * 1000);
      const initBounds = {
        start: minStart - padding,
        end: maxEnd + padding,
      };
      setBounds1(initBounds);
      setBounds2(initBounds);
      setTimelineRenderKey((prev) => prev + 1);
    } else if (rangeStartMs && rangeEndMs) {
      const initBounds = {
        start: rangeStartMs,
        end: rangeEndMs,
      };
      setBounds1(initBounds);
      setBounds2(initBounds);
      setTimelineRenderKey((prev) => prev + 1);
    }
  }, [statusTimelineItems, sitesTimelineItems, rangeStartMs, rangeEndMs]);

  // Синхронизация скролла между двумя таймлайнами
  useEffect(() => {
    if (isSynced && bounds1.start !== null) {
      setBounds2(bounds1);
    }
  }, [isSynced, bounds1]);

  const handleViewChange1 = (start, end) => {
    isZoomedRef.current = true;
    setBounds1({ start, end });
    if (isSynced) setBounds2({ start, end });
  };

  const handleViewChange2 = (start, end) => {
    isZoomedRef.current = true;
    setBounds2({ start, end });
    if (isSynced) setBounds1({ start, end });
  };

  // Красная полоса воспроизведения (Playhead)
  const getMarkerStyle = () => {
    if (!currentTimeMs || !bounds1.start || !bounds1.end) return { display: "none" };
    const totalViewMs = bounds1.end - bounds1.start;
    const offsetMs = currentTimeMs - bounds1.start;
    const percent = (offsetMs / totalViewMs) * 100;
    if (percent < 0 || percent > 100) return { display: "none" };

    return {
      left: `${percent}%`,
      position: "absolute",
      top: "35px",
      bottom: "0",
      width: "2px",
      backgroundColor: "#e55353",
      zIndex: 100,
      pointerEvents: "none",
      boxShadow: "0 0 6px rgba(229, 83, 83, 0.8)",
    };
  };

  return (
    <CRow className="mb-4">
      <CCol xs={12}>
        <CCard className="shadow-sm border-0">
          <CCardHeader className="bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
            <span>Временная шкала смены</span>
            <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", margin: 0 }} className="text-muted small">
              <input
                type="checkbox"
                checked={isSynced}
                onChange={(e) => setIsSynced(e.target.checked)}
              />
              Синхронизировать шкалы
            </label>
          </CCardHeader>

          <CCardBody>
            {bounds1.start !== null ? (
              <div className="position-relative">
                {/* КРАСНЫЙ МАРКЕР ВРЕМЕНИ (СКРУББЕР) */}
                <div style={getMarkerStyle()}>
                  <div
                    style={{
                      position: "absolute",
                      top: "-6px",
                      left: "-5px",
                      width: "12px",
                      height: "12px",
                      backgroundColor: "#e55353",
                      borderRadius: "50%",
                      border: "2px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  />
                </div>

                {/* 1. ВЕРХНИЙ ТАЙМЛАЙН: СОСТОЯНИЯ И СКОРОСТИ */}
                <div className="mb-3">
                  <div className="fw-bold text-muted small mb-1">
                    1. Состояния техники и скорость (погрузка в приоритете):
                  </div>
                  {statusTimelineItems.length > 0 ? (
                    <CustomTimeline
                      key={`tl1_${timelineRenderKey}`}
                      items={statusTimelineItems}
                      initialStart={bounds1.start}
                      initialEnd={bounds1.end}
                      onViewChange={handleViewChange1}
                      onTimeDoubleClick={(e) => handleTimeDoubleClick({ exactTime: e.exactTime })}
                    />
                  ) : (
                    <div className="p-3 text-center text-muted small border rounded bg-light">
                      Нет данных о состояниях за выбранный период
                    </div>
                  )}
                </div>

                {/* 2. НИЖНИЙ ТАЙМЛАЙН: СЕССИИ ПОГРУЗКИ */}
                <div>
                  <div className="fw-bold text-muted small mb-1">
                    2. Сессии погрузки:
                  </div>
                  {sitesTimelineItems.length > 0 ? (
                    <CustomTimeline
                      key={`tl2_${timelineRenderKey}`}
                      items={sitesTimelineItems}
                      initialStart={bounds2.start}
                      initialEnd={bounds2.end}
                      onViewChange={handleViewChange2}
                      onTimeDoubleClick={(e) => handleTimeDoubleClick({ exactTime: e.exactTime })}
                    />
                  ) : (
                    <div className="p-3 text-center text-muted small border rounded bg-light">
                      Сессий погрузки за выбранный период не зафиксировано
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center p-3 text-muted">
                Выберите период и нажмите "Получить аналитику"
              </div>
            )}
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

GarbageTimelineSection.propTypes = {
  dateRangeForVis: PropTypes.object,
  statusTimelineItems: PropTypes.array,
  sitesTimelineItems: PropTypes.array,
  handleTimeDoubleClick: PropTypes.func.isRequired,
  currentTimeMs: PropTypes.number,
};

export default GarbageTimelineSection;